/**
 * ReportsDialogs — single mount point for all Reports v2 dialogs.
 *
 * Both the list view and the detail view need to render the same set of
 * dialogs (Edit / Duplicate / Share / Schedule / Export / Filter /
 * EditIndicator / Delete). To keep `Reports.tsx` under the 500-line cap,
 * we lift the dialog wiring here and let it consume `useReports()` state
 * via props.
 */

import {
  DeleteReportDialog,
  DownloadAuditDialog,
  DuplicateReportDialog,
  EditIndicatorDialog,
  EditReportDialog,
  ExportDialog,
  FilterDialog,
  FiscalCalendarDialog,
  PublishReportDialog,
  RevisionHistoryDialog,
  ScheduleReportDialog,
  ShareReportDialog,
} from './dialogs';
import {
  type AnalyseCategory,
  type AnalyseDashboard,
  type DashboardIndicator,
  type Dataset,
  type ScheduledReportCreatePayload,
} from '../../lib/api/reporting';
import type { ExportFormat } from './useReports';

interface ReportsDialogsProps {
  // Selection / context
  selectedReport: AnalyseDashboard | null;
  firstIndicatorId: number | null;
  categories: AnalyseCategory[];
  datasets: Dataset[];
  saving: boolean;

  // Edit
  isEditOpen: boolean;
  setIsEditOpen: (open: boolean) => void;
  reportToEdit: AnalyseDashboard | null;
  onEditReport: (payload: Partial<AnalyseDashboard>) => void;

  // Duplicate
  isDuplicateOpen: boolean;
  setIsDuplicateOpen: (open: boolean) => void;
  reportToDuplicate: AnalyseDashboard | null;
  onDuplicateReport: (name: string) => void;

  // Share
  isShareOpen: boolean;
  setIsShareOpen: (open: boolean) => void;
  reportToShare: AnalyseDashboard | null;
  onShareReport: (sharedWith: AnalyseDashboard['shared_with']) => Promise<void> | void;

  // Publish
  isPublishOpen: boolean;
  setIsPublishOpen: (open: boolean) => void;
  reportToPublish: AnalyseDashboard | null;

  // Revision history
  isRevisionsOpen: boolean;
  setIsRevisionsOpen: (open: boolean) => void;
  reportForRevisions: AnalyseDashboard | null;

  // Download audit
  isDownloadLogsOpen: boolean;
  setIsDownloadLogsOpen: (open: boolean) => void;

  // Fiscal calendar
  isFiscalCalendarOpen: boolean;
  setIsFiscalCalendarOpen: (open: boolean) => void;

  // Schedule
  isScheduleOpen: boolean;
  setIsScheduleOpen: (open: boolean) => void;
  onScheduleReport: (payload: ScheduledReportCreatePayload) => void;

  // Export
  isExportOpen: boolean;
  setIsExportOpen: (open: boolean) => void;
  onExportReport: (format: ExportFormat, applyFilters: boolean, indicatorIds?: number[]) => void;

  // Filter
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  onAfterFilterChange: () => void;

  // Edit Indicator
  isEditIndicatorOpen: boolean;
  setIsEditIndicatorOpen: (open: boolean) => void;
  indicatorToEdit: DashboardIndicator | null;
  onEditIndicator: (payload: Partial<DashboardIndicator>) => void;
  onOpenIndicatorBuilder: (indicator: DashboardIndicator) => void;

  // Delete
  isDeleteReportOpen: boolean;
  setIsDeleteReportOpen: (open: boolean) => void;
  reportToDelete: AnalyseDashboard | null;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

export function ReportsDialogs(props: ReportsDialogsProps) {
  return (
    <>
      <EditReportDialog
        open={props.isEditOpen}
        onOpenChange={props.setIsEditOpen}
        report={props.reportToEdit}
        categories={props.categories}
        datasets={props.datasets}
        saving={props.saving}
        onSubmit={props.onEditReport}
      />

      <DuplicateReportDialog
        open={props.isDuplicateOpen}
        onOpenChange={props.setIsDuplicateOpen}
        report={props.reportToDuplicate}
        saving={props.saving}
        onSubmit={props.onDuplicateReport}
      />

      <ShareReportDialog
        open={props.isShareOpen}
        onOpenChange={props.setIsShareOpen}
        report={props.reportToShare}
        saving={props.saving}
        onSaveSharedWith={props.onShareReport}
      />

      <PublishReportDialog
        open={props.isPublishOpen}
        onOpenChange={props.setIsPublishOpen}
        report={props.reportToPublish}
      />

      <RevisionHistoryDialog
        open={props.isRevisionsOpen}
        onOpenChange={props.setIsRevisionsOpen}
        report={props.reportForRevisions}
      />

      <DownloadAuditDialog
        open={props.isDownloadLogsOpen}
        onOpenChange={props.setIsDownloadLogsOpen}
      />

      <FiscalCalendarDialog
        open={props.isFiscalCalendarOpen}
        onOpenChange={props.setIsFiscalCalendarOpen}
      />

      <ScheduleReportDialog
        open={props.isScheduleOpen}
        onOpenChange={props.setIsScheduleOpen}
        report={props.selectedReport}
        saving={props.saving}
        onSubmit={props.onScheduleReport}
      />

      <ExportDialog
        open={props.isExportOpen}
        onOpenChange={props.setIsExportOpen}
        report={props.selectedReport}
        onDownload={props.onExportReport}
      />

      <FilterDialog
        open={props.isFilterOpen}
        onOpenChange={props.setIsFilterOpen}
        indicatorId={props.firstIndicatorId}
        onApplied={props.onAfterFilterChange}
      />

      <EditIndicatorDialog
        open={props.isEditIndicatorOpen}
        onOpenChange={props.setIsEditIndicatorOpen}
        indicator={props.indicatorToEdit}
        saving={props.saving}
        onSubmit={props.onEditIndicator}
        onOpenBuilder={props.onOpenIndicatorBuilder}
      />

      <DeleteReportDialog
        open={props.isDeleteReportOpen}
        onOpenChange={props.setIsDeleteReportOpen}
        report={props.reportToDelete}
        onCancel={props.onCancelDelete}
        onConfirm={props.onConfirmDelete}
      />
    </>
  );
}
